import React, { useCallback, useMemo, useState } from 'react'

import { useCanvasSelection } from '@sketch/canvas'
import { ReactComponent as ChevronIcon } from '@sketch/icons/chevron-right-16'

import { useCompare } from '../../CompareContext'
import { useRevealChange } from '../../useRevealChange'
import { describeChanges } from '../../describeChange'
import {
  groupLayerChangesByEntity,
  unattributedLayerChanges,
} from '../../groupChanges'
import { EntityChange, LayerChange } from '../../layerTypes'
import { EntityChangeIcon, LayerChangeIcon } from '../../changeIcon'
import * as Styled from './ChangesPanel.styles'

/**
 * What an entity's own change amounts to, in one line. Said from the newer
 * version whichever of the two is open, because "this version" means the
 * opposite thing depending on where the reader is standing.
 */
function describeEntity(change: EntityChange) {
  if (change.kind === 'added') return 'Only in the newer version'
  if (change.kind === 'removed') return 'Only in the older version'

  const count = change.layerChangeCount
  const inside =
    count === 0
      ? ''
      : count === 1
        ? '1 layer changed'
        : `${count} layers changed`

  // Both, when both happened: a frame renamed with three layers changed inside
  // it is not described by either half alone.
  return [describeChanges(change.changes), inside].filter(Boolean).join(', ')
}

/** As describeEntity, and named from the same side. */
const describeLayer = (change: LayerChange) => {
  if (change.kind === 'added') return 'Only in the newer version'
  if (change.kind === 'removed') return 'Only in the older version'

  return describeChanges(change.changes)
}

/**
 * One changed layer, wherever it is listed.
 *
 * The same row whether it sits under the frame that holds it or in the group
 * for the layers that belong to no frame at all - a change is the same thing to
 * point at and click either way, and giving the loose ones a row of their own
 * would be two things to keep in step for no reason a reader could see.
 */
function LayerRow({ layer }: { layer: LayerChange }) {
  const reveal = useRevealChange()
  const { selection, selectLayerByPathHash } = useCanvasSelection()

  const isSelected =
    selection?.kind === 'layer' &&
    selection.layerPathHash === layer.layerPathHash

  return (
    <Styled.ChangeButton
      type="button"
      $nested
      $selected={isSelected}
      aria-pressed={isSelected}
      onClick={() => {
        selectLayerByPathHash(layer.uuid, layer.layerPathHash)
        reveal({
          layerPathHash: layer.layerPathHash,
          // A deleted layer has no node here; the frame it was deleted from
          // does, so that is where the camera goes. Null for a loose layer,
          // which leaves the camera where it is rather than travelling to a
          // frame that was never involved.
          entityUUID: layer.entityUUID,
        })
      }}
    >
      <Styled.RowIcon aria-hidden>
        <LayerChangeIcon change={layer} />
      </Styled.RowIcon>
      <Styled.Text>
        <Styled.Name $selected={isSelected}>
          {layer.name || 'Untitled'}
        </Styled.Name>
        <Styled.What>{describeLayer(layer)}</Styled.What>
      </Styled.Text>
    </Styled.ChangeButton>
  )
}

/** The panel with only a message in it, for every state short of a result. */
function ChangesMessage({
  children,
  busy,
}: {
  children: React.ReactNode
  busy?: boolean
}) {
  return (
    <Styled.Wrapper aria-busy={busy || undefined}>
      <Styled.Header>
        <Styled.Title>Changes</Styled.Title>
      </Styled.Header>
      <Styled.Message>{children}</Styled.Message>
    </Styled.Wrapper>
  )
}

/**
 * Everything the comparison found, as a list you can click through.
 *
 * The canvas shows the page; this says what is different about it. Seeing
 * which frame changed never tells you why, and the frames worth looking at
 * first are rarely the ones nearest the viewport.
 *
 * Grouped by entity with its layers beneath, because a change is almost always
 * looked for as "what happened to this screen" rather than as a flat list of
 * layers that happens to span the page.
 *
 * Everything here is said from the newer of the two versions, whichever one the
 * reader is looking at: "added" means the newer version has it. Phrasing it
 * relative to the canvas instead made the same change swap names depending on
 * which version was open, so a frame that had just been added announced itself
 * as removed to anyone reading from the older side.
 */
export function ChangesPanel() {
  const comparison = useCompare()
  const reveal = useRevealChange()

  // The canvas's own selection, so a change picked from this list is lit up
  // exactly as the same thing clicked on the canvas would be - one selection,
  // one treatment, and the overlays already know how to draw it.
  const { selection, selectEntityByUUID } = useCanvasSelection()

  const entities = comparison?.entities
  const layers = comparison?.layers

  const layersByEntity = useMemo(
    () => groupLayerChangesByEntity(layers ?? []),
    [layers]
  )

  /** The changes that belong to no frame or Section. */
  const loose = useMemo(() => unattributedLayerChanges(layers ?? []), [layers])

  /**
   * Which entities have been folded away, by uuid.
   *
   * Open is the default: the panel exists to say what changed, and making the
   * reader click each frame to find out would hide the answer they came for.
   * Folding is for the frame with forty changed layers in it, where the summary
   * on the row above already says as much as a reader scanning wants.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set())

  const toggle = useCallback(
    (uuid: string) =>
      setCollapsed(open => {
        const next = new Set(open)

        if (!next.delete(uuid)) next.add(uuid)

        return next
      }),
    []
  )

  // Nothing to say when no comparison is running - the panel is not a
  // permanent fixture of the sidebar.
  if (!comparison?.versions) return null

  if (comparison.loading) {
    return <ChangesMessage busy>Reading both versions…</ChangesMessage>
  }

  if (comparison.error) {
    return (
      <ChangesMessage>
        These versions could not be compared. Reload to try again.
      </ChangesMessage>
    )
  }

  // Before "nothing changed", as is the next one: a page that is not there
  // would otherwise compare as a page with nothing on it.
  if (comparison.isPageMissing) {
    return (
      <ChangesMessage>
        This page isn’t in the version you’re comparing with.
      </ChangesMessage>
    )
  }

  // Before "nothing changed": a version without its presentation file reads as
  // an empty page, and would compare as unchanged.
  if (comparison.isAwaitingAssets) {
    return (
      <ChangesMessage>
        One of these versions is still being prepared. Reload to try again.
      </ChangesMessage>
    )
  }

  if (!entities?.length && !loose.length) {
    return (
      <ChangesMessage>
        {comparison.isPartial
          ? 'Part of this page could not be read, so it could not be fully compared. Reload to try again.'
          : 'Nothing changed on this page.'}
      </ChangesMessage>
    )
  }

  const total = (entities?.length ?? 0) + loose.length

  return (
    <Styled.Wrapper data-testid="compare-changes-panel">
      <Styled.Header>
        <Styled.Title>Changes</Styled.Title>
        <Styled.Count>
          {/* Every row the reader can click, not just the entities - a count
          that leaves the loose layers out disagrees with the list under it. */}
          {total === 1 ? '1 item' : `${total} items`}
        </Styled.Count>
      </Styled.Header>

      <Styled.List>
        {(entities ?? []).map(entity => {
          const inside = layersByEntity.get(entity.uuid) ?? []
          const isSelected =
            selection?.kind !== 'layer' && selection?.uuid === entity.uuid
          const isOpen = !collapsed.has(entity.uuid)

          return (
            <Styled.Row key={entity.uuid}>
              <Styled.RowHeader>
                {/* Only where there is something to fold: a twist-down that
                does nothing is a control the reader has to try to learn. */}
                {inside.length > 0 ? (
                  <Styled.Disclosure
                    type="button"
                    $open={isOpen}
                    aria-expanded={isOpen}
                    aria-label={`${isOpen ? 'Hide' : 'Show'} the layers changed in ${
                      entity.name || 'Untitled'
                    }`}
                    onClick={() => toggle(entity.uuid)}
                  >
                    <ChevronIcon aria-hidden />
                  </Styled.Disclosure>
                ) : (
                  <Styled.DisclosureSpacer aria-hidden />
                )}

                <Styled.ChangeButton
                  type="button"
                  $selected={isSelected}
                  aria-pressed={isSelected}
                  onClick={() => {
                    selectEntityByUUID(entity.uuid, entity.entityKind)
                    reveal({ uuid: entity.uuid })
                  }}
                >
                  <Styled.RowIcon aria-hidden>
                    <EntityChangeIcon change={entity} />
                  </Styled.RowIcon>
                  <Styled.Text>
                    <Styled.Name $selected={isSelected}>
                      {entity.name || 'Untitled'}
                    </Styled.Name>
                    <Styled.What>{describeEntity(entity)}</Styled.What>
                    {entity.kind !== 'modified' && (
                      <Styled.Tag $kind={entity.kind}>{entity.kind}</Styled.Tag>
                    )}
                  </Styled.Text>
                </Styled.ChangeButton>
              </Styled.RowHeader>

              {isOpen &&
                inside.map(layer => (
                  <LayerRow key={layer.layerPathHash} layer={layer} />
                ))}
            </Styled.Row>
          )
        })}

        {/* Last, because a frame or a Section is how a reader looks for a
        change and a loose layer is the exception. Given a heading of its own so
        the rows beneath are not read as frames, which is what they would look
        like listed flat among them. */}
        {loose.length > 0 && (
          <Styled.Row data-testid="compare-loose-changes">
            <Styled.GroupLabel>On the page</Styled.GroupLabel>
            {loose.map(layer => (
              <LayerRow key={layer.layerPathHash} layer={layer} />
            ))}
          </Styled.Row>
        )}
      </Styled.List>
    </Styled.Wrapper>
  )
}
